import React, { useState, useEffect, useRef } from 'react';
import { FaComment, FaCamera, FaCommentDots } from 'react-icons/fa';
import Sheet from 'react-modal-sheet';
import { addComment, beginUploadSession, endUploadSession, setUploadProgress } from '../../store/slices/eventSlice';
import { useDispatch } from 'react-redux';
import { useSelector } from 'react-redux';
import { selectAllUrlParams } from '../../store/slices/urlParamsSlice';
import { addCommentToShow, addImage, addVideo } from '../../store/slices/uploadedContentSlice';
import { uploadMany } from '../../utils/supabaseUpload';
import GuestIdentitySheet from '../GuestIdentitySheet/GuestIdentitySheet';

const FabMenu = () => {
  const [comment, setComment] = useState("");
  const [commentDrawerOpen, setCommentDrawerOpen] = useState(false);
  const [isKeyboardOpen, setIsKeyboardOpen] = useState(false);
  const [commentGuestName, setCommentGuestName] = useState('');
  const [commentGuestPhone, setCommentGuestPhone] = useState('');
  const [commentPostAnonymous, setCommentPostAnonymous] = useState(false);
  const [pendingImageFiles, setPendingImageFiles] = useState([]);
  const [pendingVideoFiles, setPendingVideoFiles] = useState([]);
  const [identitySheetOpen, setIdentitySheetOpen] = useState(false);
  const textareaRef = useRef(null);
  const galleryInputRef = useRef(null);
  const dispatch = useDispatch();
  const urlParams = useSelector(selectAllUrlParams);
  const theme = urlParams?.templateId || urlParams?.theme || 'event';
  const isRestaurantTheme = theme === 'restaurant';

  useEffect(() => {
    const handleViewportChange = () => {
      if (window.visualViewport) {
        const viewportHeight = window.visualViewport.height;
        const windowHeight = window.innerHeight;
        // If viewport is significantly smaller, keyboard is likely open
        const keyboardOpen = viewportHeight < windowHeight * 0.75;
        setIsKeyboardOpen(keyboardOpen);
      }
    };

    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', handleViewportChange);
      return () => {
        window.visualViewport.removeEventListener('resize', handleViewportChange);
      };
    }
  }, []);

  const handleGalleryClick = () => {
    galleryInputRef.current?.click();
  };

  const handleGalleryFileChange = (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    const imageFiles = files.filter(f => f.type.startsWith('image/'));
    const videoFiles = files.filter(f => f.type.startsWith('video/'));
    setPendingImageFiles(imageFiles);
    setPendingVideoFiles(videoFiles);
    setIdentitySheetOpen(true);
    if (galleryInputRef.current) galleryInputRef.current.value = '';
  };

  const handleIdentitySubmit = async ({ guestName, guestPhone }) => {
    const imageFiles = pendingImageFiles;
    const videoFiles = pendingVideoFiles;
    setPendingImageFiles([]);
    setPendingVideoFiles([]);
    setIdentitySheetOpen(false);

    const allFiles = [...imageFiles, ...videoFiles];
    const totalFiles = allFiles.length;
    const totalBytes = allFiles.reduce((sum, f) => sum + (f.size || 0), 0);
    const qrToken = urlParams.token;

    const baseMeta = {
      eventId: urlParams.eventId,
      eventCode: urlParams.eventCode,
      guestName: guestName ?? null,
      guestPhone: guestPhone ?? null,
    };

    dispatch(beginUploadSession({ totalBytes, totalFiles }));

    let filesCompleted = 0;
    const failures = [];

    const onFileDone = (file, result, dispatchAction) => {
      filesCompleted += 1;
      dispatch(setUploadProgress({
        loaded: Math.round((filesCompleted / totalFiles) * totalBytes),
        total: totalBytes,
        filesCompleted,
        filesTotal: totalFiles,
      }));
      dispatch(dispatchAction({
        id: result.id || Date.now().toString() + Math.random().toString(36).substring(2),
        url: result.url,
        thumbUrl: result.thumbUrl ?? null,
        name: file.name,
        size: file.size,
        uploadedAt: result.uploadedAt || new Date().toISOString(),
        eventCode: urlParams.eventCode,
        guestName: guestName ?? null,
        guestPhone: guestPhone ?? null,
      }));
    };

    if (imageFiles.length > 0) {
      try {
        await uploadMany(
          imageFiles,
          { ...baseMeta, mediaType: 'image' },
          qrToken,
          {
            concurrency: 4,
            onFileDone: (i, result) => onFileDone(imageFiles[i], result, addImage),
          }
        );
      } catch (err) {
        console.error('Image upload failed:', err);
        const failedCount = err?.failures?.length || imageFiles.length;
        failures.push({ kind: 'images', failedCount, error: err });
      }
    }

    if (videoFiles.length > 0) {
      try {
        await uploadMany(
          videoFiles,
          { ...baseMeta, mediaType: 'video' },
          qrToken,
          {
            concurrency: 2,
            onFileDone: (i, result) => onFileDone(videoFiles[i], result, addVideo),
          }
        );
      } catch (err) {
        console.error('Video upload failed:', err);
        const failedCount = err?.failures?.length || videoFiles.length;
        failures.push({ kind: 'videos', failedCount, error: err });
      }
    }

    dispatch(endUploadSession({ success: failures.length === 0 }));

    if (failures.length > 0) {
      const formatUploadError = (error) => {
        if (typeof error === 'string') return error;
        return (
          error?.response?.data?.error ||
          error?.response?.data?.message ||
          error?.message ||
          error?.error ||
          'Unknown error'
        );
      };
      const parts = failures.map((f) => {
        return `${f.failedCount} ${f.kind} failed (${formatUploadError(f.error)})`;
      });
      alert(`Some uploads failed: ${parts.join('; ')}`);
    }
  };

  const handleUploadComment = async () => {
    if (comment.length === 0) {
      alert('Please Write a comment');
      return;
    }

    const guestName = commentPostAnonymous ? null : (commentGuestName.trim() || null);
    const guestPhone = commentPostAnonymous ? null : (isRestaurantTheme ? (commentGuestPhone.trim() || null) : null);

    const formData = new FormData();
    formData.append('content', comment);
    formData.append('mediaType', 'comment');
    Object.keys(urlParams).forEach(key => {
      if (urlParams[key] !== null && urlParams[key] !== undefined) {
        formData.append(key, urlParams[key]);
      }
    });
    if (guestName != null) formData.append('guestName', guestName);
    if (guestPhone != null) formData.append('guestPhone', guestPhone);

    try {
      await dispatch(addComment(formData)).unwrap();
      dispatch(addCommentToShow({
        text: comment,
        guestName,
        guestPhone,
        eventCode: urlParams.eventCode,
      }));
      setComment('');
      setCommentGuestName('');
      setCommentGuestPhone('');
      setCommentPostAnonymous(false);
      setCommentDrawerOpen(false);
    } catch (error) {
      alert('Failed to upload Comment: ' + (error.message || 'Unknown error'));
    }
  };

  return (
    <>
      {isRestaurantTheme ? (
        // Restaurant Theme: Two buttons side by side
        <div style={{ 
          display: 'flex', 
          flexDirection: 'row',
          gap: '10px', 
          justifyContent: 'center',
          alignItems: 'center',
          flexWrap: 'nowrap',
          width: '100%'
        }}>
          <button
            onClick={handleGalleryClick}
            style={{
              backgroundColor: 'white',
              border: 'none',
              borderRadius: '10px',
              padding: '10px 12px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '10px',
              cursor: 'pointer',
              transition: 'all 0.2s ease',
              outline: 'none',
              fontFamily: 'Inter, Poppins, sans-serif',
              fontSize: '13px',
              fontWeight: '600',
              color: '#166534',
              boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
              width: 'auto',
              maxWidth: '160px',
            }}
            onMouseEnter={(e) => {
              e.target.style.transform = 'translateY(-2px)';
              e.target.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.15)';
              e.target.style.backgroundColor = '#f0fdf4';
            }}
            onMouseLeave={(e) => {
              e.target.style.transform = 'translateY(0)';
              e.target.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.1)';
              e.target.style.backgroundColor = 'white';
            }}
          >
            <FaCamera style={{ fontSize: '16px', color: '#166534' }} />
            <span>SHOW & SHARE</span>
          </button>

          <button
            onClick={() => setCommentDrawerOpen(true)}
            style={{
              backgroundColor: 'white',
              border: 'none',
              borderRadius: '10px',
              padding: '10px 12px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '6px',
              cursor: 'pointer',
              transition: 'all 0.2s ease',
              outline: 'none',
              fontFamily: 'Inter, Poppins, sans-serif',
              fontSize: '13px',
              fontWeight: '600',
              color: '#166534',
              boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
              width: 'auto',
              maxWidth: '160px',
            }}
            onMouseEnter={(e) => {
              e.target.style.transform = 'translateY(-2px)';
              e.target.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.15)';
              e.target.style.backgroundColor = '#f0fdf4';
            }}
            onMouseLeave={(e) => {
              e.target.style.transform = 'translateY(0)';
              e.target.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.1)';
              e.target.style.backgroundColor = 'white';
            }}
          >
            <FaCommentDots style={{ fontSize: '16px', color: '#166534' }} />
            <span>WRITE REVIEW</span>
          </button>
        </div>
      ) : (
        // Event theme: camera + comment buttons (no + menu)
        <div style={eventButtonsRowStyle}>
          <button
            type="button"
            onClick={handleGalleryClick}
            aria-label="رفع صور أو فيديو"
            style={eventIconButtonStyle}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-2px)';
              e.currentTarget.style.boxShadow = '0 6px 24px rgba(0, 0, 0, 0.2), 0 3px 10px rgba(0, 0, 0, 0.15)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '0 4px 20px rgba(0, 0, 0, 0.15), 0 2px 8px rgba(0, 0, 0, 0.1)';
            }}
          >
            <FaCamera style={{ fontSize: '22px', color: '#2c3e50', pointerEvents: 'none' }} />
          </button>

          <button
            type="button"
            onClick={() => setCommentDrawerOpen(true)}
            aria-label="كتابة تعليق"
            style={eventIconButtonStyle}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-2px)';
              e.currentTarget.style.boxShadow = '0 6px 24px rgba(0, 0, 0, 0.2), 0 3px 10px rgba(0, 0, 0, 0.15)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '0 4px 20px rgba(0, 0, 0, 0.15), 0 2px 8px rgba(0, 0, 0, 0.1)';
            }}
          >
            <FaComment style={{ fontSize: '22px', color: '#2c3e50', pointerEvents: 'none' }} />
          </button>
        </div>
      )}

      {/* Comment Drawer */}
      <Sheet
        isOpen={commentDrawerOpen}
        // Prevent swipe-down/scroll from closing the sheet.
        // Close only via backdrop tap or explicit UI actions.
        onClose={() => {}}
        snapPoints={isKeyboardOpen ? [0.95] : [0.95, 0.75, 0.45]}
        initialSnap={0}
      >
        <Sheet.Container>
          <Sheet.Header />
          <Sheet.Content style={{ paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 20px)' }}>
            <Sheet.Scroller>
              <div style={{ padding: 20, paddingTop: isKeyboardOpen ? '10px' : '20px' }}>
              {/* Optional name / phone for comment (event: name only; restaurant: name + phone) */}
              <div style={{ marginBottom: 12 }}>
                <label style={{ display: 'block', marginBottom: 4, fontSize: 14, color: '#555' }}>
                  {isRestaurantTheme ? 'Your name (optional)' : 'اسمك (اختياري)'}
                </label>
                <input
                  type="text"
                  value={commentGuestName}
                  onChange={(e) => setCommentGuestName(e.target.value)}
                  placeholder={isRestaurantTheme ? 'Name' : 'الاسم'}
                  disabled={commentPostAnonymous}
                  style={{ width: '100%', maxWidth: 400, padding: '10px 12px', borderRadius: 10, border: '2px solid #e0e0e0', fontSize: 16, boxSizing: 'border-box' }}
                />
              </div>
              {isRestaurantTheme && (
                <div style={{ marginBottom: 12 }}>
                  <label style={{ display: 'block', marginBottom: 4, fontSize: 14, color: '#555' }}>Phone (optional)</label>
                  <input
                    type="tel"
                    value={commentGuestPhone}
                    onChange={(e) => setCommentGuestPhone(e.target.value)}
                    placeholder="Phone number"
                    disabled={commentPostAnonymous}
                    style={{ width: '100%', maxWidth: 400, padding: '10px 12px', borderRadius: 10, border: '2px solid #e0e0e0', fontSize: 16, boxSizing: 'border-box' }}
                  />
                </div>
              )}
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16, cursor: 'pointer' }}>
                <input type="checkbox" checked={commentPostAnonymous} onChange={(e) => setCommentPostAnonymous(e.target.checked)} />
                <span style={{ fontSize: 14, color: '#555' }}>{isRestaurantTheme ? 'Post as anonymous' : 'نشر بدون اسم'}</span>
              </label>
              <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '16px' }}>
                <textarea
                  ref={textareaRef}
                  placeholder={isRestaurantTheme ? 'Write your review...' : 'اكتب تعليقك هنا...'}
                  value={comment}
                  onChange={(e) => setComment(e.target.value)}
                  style={{
                    width: '70%',
                    maxWidth: '400px',
                    height: '120px',
                    borderRadius: '16px',
                    padding: '16px',
                    border: '2px solid #e0e0e0',
                    fontSize: '16px',
                    resize: 'none',
                    fontFamily: 'inherit',
                    transition: 'border-color 0.2s ease, box-shadow 0.2s ease',
                    outline: 'none',
                    direction: 'rtl',
                  }}
                  onFocus={(e) => {
                    setIsKeyboardOpen(true);
                    e.target.style.borderColor = '#2c3e50';
                    e.target.style.boxShadow = '0 0 0 3px rgba(44, 62, 80, 0.1)';
                    // Scroll textarea into view after keyboard animation
                    setTimeout(() => {
                      if (textareaRef.current) {
                        textareaRef.current.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'nearest' });
                      }
                    }, 400);
                  }}
                  onBlur={(e) => {
                    // Delay to allow for button clicks
                    setTimeout(() => {
                      setIsKeyboardOpen(false);
                    }, 200);
                    e.target.style.borderColor = '#e0e0e0';
                    e.target.style.boxShadow = 'none';
                  }}
                />
              </div>
              <div style={{ textAlign: 'center', marginTop: '20px' }}>
                <button
                  style={{
                    backgroundColor: isRestaurantTheme ? '#2c3e50' : '#2c3e50',
                    color: 'white',
                    padding: isRestaurantTheme ? '16px 40px' : '14px 32px',
                    border: 'none',
                    borderRadius: isRestaurantTheme ? '16px' : '12px',
                    fontSize: isRestaurantTheme ? '18px' : '16px',
                    fontWeight: isRestaurantTheme ? '700' : '600',
                    cursor: 'pointer',
                    boxShadow: isRestaurantTheme 
                      ? '0 6px 20px rgba(44, 62, 80, 0.4), 0 3px 10px rgba(44, 62, 80, 0.3)' 
                      : '0 4px 12px rgba(44, 62, 80, 0.3)',
                    transition: isRestaurantTheme ? 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)' : 'all 0.2s ease',
                  }}
                  onMouseEnter={(e) => {
                    if (isRestaurantTheme) {
                      e.target.style.transform = 'translateY(-3px) scale(1.02)';
                      e.target.style.boxShadow = '0 8px 24px rgba(44, 62, 80, 0.5), 0 4px 12px rgba(44, 62, 80, 0.4)';
                    } else {
                      e.target.style.transform = 'translateY(-2px)';
                      e.target.style.boxShadow = '0 6px 16px rgba(44, 62, 80, 0.4)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.transform = 'translateY(0) scale(1)';
                    e.target.style.boxShadow = isRestaurantTheme 
                      ? '0 6px 20px rgba(44, 62, 80, 0.4), 0 3px 10px rgba(44, 62, 80, 0.3)' 
                      : '0 4px 12px rgba(44, 62, 80, 0.3)';
                  }}
                  onClick={() => handleUploadComment()}
                >
                  إرسال
                </button>
              </div>
              </div>
            </Sheet.Scroller>
          </Sheet.Content>
        </Sheet.Container>
        <Sheet.Backdrop onTap={() => setCommentDrawerOpen(false)} />
      </Sheet>

      {/* Gallery Upload Input */}
      <input
        ref={galleryInputRef}
        type="file"
        accept="image/*,video/*"
        multiple
        style={{ display: 'none' }}
        data-upload-trigger
        onChange={handleGalleryFileChange}
      />

      {/* Guest identity sheet before gallery upload */}
      <GuestIdentitySheet
        isOpen={identitySheetOpen}
        onClose={() => { setIdentitySheetOpen(false); setPendingImageFiles([]); setPendingVideoFiles([]); }}
        theme={isRestaurantTheme ? 'restaurant' : 'event'}
        onSubmit={handleIdentitySubmit}
      />
    </>
  );
};

const eventButtonsRowStyle = {
  display: 'flex',
  flexDirection: 'column',
  gap: '10px',
  alignItems: 'center',
};

const eventIconButtonStyle = {
  backgroundColor: 'rgba(255, 255, 255, 0.95)',
  border: 'none',
  borderRadius: '50%',
  width: '56px',
  height: '56px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15), 0 2px 8px rgba(0, 0, 0, 0.1)',
  cursor: 'pointer',
  transition: 'all 0.2s ease',
  outline: 'none',
  WebkitTapHighlightColor: 'transparent',
};

export default FabMenu;
